import {
  Breadcrumbs,
  Notification,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useClientPolicies } from '@omnipresentgroup/domains';
import { LeaveSettingsList } from '@omnipresentgroup/features';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import type { LeaveTypeEnum } from '@omnipresentgroup/types';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import {
  CLIENT_LEAVE_SETTING_WITH_PARAMS_PATH,
  LEAVE_SETTING_PATH,
} from '../../constants';
import { LeaveSettingSummary } from '../../features/leave-settings';
import { useManagerUser } from '../../hooks';

export const LEAVE_SETTINGS_PAGE_TITLE = 'Leave settings';

const LEAVE_SETTINGS_LABEL = 'leave settings';
const NOTIFICATION_TITLE =
  'Additional countries will be included soon, and the ability to customize leave settings for these countries will be made available.';

export const LeaveSettings = () => {
  const navigate = useNavigate();

  const { companyId } = useManagerUser();

  const [searchParameters] = useSearchParams();
  const urlParameters = new URLSearchParams(searchParameters);

  const countryCode = urlParameters.get('countryCode') as string;
  const leaveType = urlParameters.get('type') as LeaveTypeEnum;

  const { data: clientCountryPolicies } = useClientPolicies({
    clientId: companyId,
    countryCode: countryCode,
    enabled: !!countryCode,
  });

  const clientCountryPolicy = useMemo(
    () => clientCountryPolicies?.[0],
    [clientCountryPolicies],
  );

  const showLeaveSettingSummary = Boolean(countryCode) && Boolean(leaveType);

  const PageHeader = () => (
    <Stack gap="4">
      <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
        {showLeaveSettingSummary && clientCountryPolicy
          ? toSentenceCase(clientCountryPolicy.country.name)
          : toSentenceCase(LEAVE_SETTINGS_LABEL)}
      </Typography>
      {showLeaveSettingSummary && clientCountryPolicy && (
        <Typography as="h3" hideParagraphSpacing size="14" color="subtle">
          {`${toSentenceCase(clientCountryPolicy.type)} leave settings`}
        </Typography>
      )}
    </Stack>
  );

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'leave-settings',
              title: `${LEAVE_SETTINGS_PAGE_TITLE}`,
              onClick:
                leaveType && countryCode
                  ? () => navigate(LEAVE_SETTING_PATH)
                  : undefined,
              active: !showLeaveSettingSummary,
            },
            ...(showLeaveSettingSummary
              ? [
                  {
                    id: 'leave-setting',
                    title: `${toSentenceCase(leaveType)} leave policy`,
                    active: Boolean(countryCode) && Boolean(leaveType),
                  },
                ]
              : []),
          ]}
        />
        <PageHeader />
      </Stack>

      {showLeaveSettingSummary && countryCode && leaveType ? (
        <LeaveSettingSummary
          companyId={companyId}
          countryCode={countryCode}
          leaveType={leaveType}
        />
      ) : (
        <>
          <Notification
            id="leave-setting-summary-notification"
            title={NOTIFICATION_TITLE}
          />
          <LeaveSettingsList
            companyId={companyId}
            columnVisibility={{
              country: true,
              category: true,
              leaveType: true,
              policyDescription: true,
            }}
            onRowClick={(clientPolicy) => {
              navigate(
                CLIENT_LEAVE_SETTING_WITH_PARAMS_PATH(
                  clientPolicy.country.code,
                  clientPolicy.type,
                ),
              );
            }}
          />
        </>
      )}
    </>
  );
};
